// Workspace Activity Feed — surfaces what FOREIGN collaborators (other tenants
// or guest users) did inside the workspaces this user owns. Activity is rolled
// up by actor + 60-min window so a consultant doing 50 small edits shows as a
// single "made 50 changes" entry instead of 50 lines of noise.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Activity as ActivityIcon, Folder, Clock } from "lucide-react";

function timeAgo(iso) {
    if (!iso) return "—";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
}

function actionsLabel(actionsMap = {}) {
    const total = Object.values(actionsMap).reduce((s, n) => s + n, 0);
    if (total <= 1) {
        const k = Object.keys(actionsMap)[0] || "edit";
        return k.replace(/_/g, " ");
    }
    return `${total} change${total === 1 ? "" : "s"}`;
}

export default function WorkspaceFeedWidget() {
    const [items, setItems] = useState(null);
    const navigate = useNavigate();
    useEffect(() => {
        api.get("/home/widget/workspace-feed?window_minutes=60&limit=20")
           .then((r) => setItems(r.data?.items || []))
           .catch(() => setItems([]));
    }, []);

    return (
        <div className="border border-border rounded-sm bg-card p-4 h-full" data-testid="home-widget-workspace-feed">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                    <ActivityIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="eyebrow text-[10px]">Activity in your workspaces</span>
                </div>
                {items && items.length > 0 && (
                    <span className="font-mono text-[10px] text-muted-foreground">{items.length}</span>
                )}
            </div>
            {items === null && <div className="text-xs text-muted-foreground">Loading…</div>}
            {items && items.length === 0 && (
                <div className="text-xs text-muted-foreground py-4 text-center" data-testid="home-widget-workspace-feed-empty">
                    No collaborator activity yet. When someone else edits one of your workspaces, you'll see it here.
                </div>
            )}
            {items && items.length > 0 && (
                <ul className="space-y-1.5 max-h-[320px] overflow-y-auto scrollbar-thin pr-1">
                    {items.map((it, i) => (
                        <li key={i}
                            className="text-[12px] leading-snug border-l-2 border-border pl-2.5 py-1 hover:bg-secondary/30 cursor-pointer rounded-sm"
                            onClick={() => navigate(`/workspace?ws=${it.workspace_id}`)}
                            data-testid={`workspace-feed-${i}`}>
                            <div className="flex items-center gap-1.5">
                                <span className="font-medium truncate">{it.user_name}</span>
                                <span className="text-muted-foreground">made</span>
                                <span className="font-mono text-[11px]">{actionsLabel(it.actions)}</span>
                                <span className="text-muted-foreground">in</span>
                                <Folder className="h-2.5 w-2.5 text-muted-foreground" />
                                <span className="font-mono text-[11px] truncate">{it.workspace_name}</span>
                            </div>
                            <div className="flex items-center gap-1 text-[10px] font-mono text-muted-foreground/80 mt-0.5">
                                <Clock className="h-2.5 w-2.5" />
                                {timeAgo(it.last_at)}
                                {it.count > 1 && (
                                    <span className="ml-2">·  rolled up over {Math.round((new Date(it.last_at) - new Date(it.first_at)) / 60000)}m</span>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
